import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { sanitizeStrict } from '../lib/sanitize';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Loader2, Printer, FileText, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PublicQuote() {
    const { t } = useTranslation();
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const res = await axios.get(`${BACKEND_URL}/api/public/preventivi/${token}`);
                if (active) setData(res.data);
            } catch (e) {
                if (active) setError(e.response?.status === 404 ? 'not_found' : 'error');
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [token]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--foreground))] py-8 px-4">
                <div className="max-w-3xl mx-auto space-y-4">
                    <Skeleton className="h-12 w-1/2" />
                    <Skeleton className="h-32 w-full" />
                    <Skeleton className="h-64 w-full" />
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))] text-[hsl(var(--foreground))] p-6">
                <Card className="p-8 text-center max-w-md" data-testid="public-quote-error">
                    <AlertCircle className="w-12 h-12 mx-auto mb-3 text-[hsl(var(--destructive))]" />
                    <h1 className="font-display text-2xl mb-2">{t('common.error') || 'Errore'}</h1>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">
                        {error === 'not_found' ? (t('pages.prev_public_not_found') || 'Preventivo non trovato o link scaduto.') : (t('common.error_load') || 'Errore di caricamento.')}
                    </p>
                </Card>
            </div>
        );
    }

    const c = data.contenuto || {};
    return (
        <div className="min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--foreground))] py-8 px-4 print:py-0">
            <div className="max-w-3xl mx-auto">
                <div className="flex items-center justify-between mb-6 no-print">
                    <div className="flex items-center gap-2 text-[hsl(var(--accent))]">
                        <FileText className="w-5 h-5" />
                        <span className="font-display text-xl">CantiereAI</span>
                    </div>
                    <Button onClick={() => window.print()} variant="outline" size="sm" className="gap-2" data-testid="public-quote-print">
                        <Printer className="w-4 h-4" /> {t('common.print') || 'Stampa'}
                    </Button>
                </div>

                <Card className="p-6 md:p-8" data-testid="public-quote">
                    <div className="flex items-start justify-between mb-6 pb-4 border-b border-[hsl(var(--border))]">
                        <div>
                            <div className="text-xs text-[hsl(var(--muted-foreground))] uppercase tracking-wider">{t('pages.prev_col_numero')}</div>
                            <div className="font-mono text-lg">{data.numero}</div>
                            <div className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">{t('pages.prev_col_cliente')}</div>
                            <div className="font-semibold">{data.cliente}</div>
                            {data.indirizzo && <div className="text-sm text-[hsl(var(--muted-foreground))]">{data.indirizzo}</div>}
                        </div>
                        <Badge variant="secondary">{data.categoria}</Badge>
                    </div>

                    <h1 className="font-display text-2xl md:text-3xl mb-2">{c.titolo}</h1>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">{c.descrizione_lavoro}</p>

                    {/* Voci - table on desktop, cards on mobile */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-[hsl(var(--muted))]">
                                <tr>
                                    <th className="p-2 text-left">{t('pages.prev_col_tipo')}</th>
                                    <th className="p-2 text-left">{t('pages.prev_col_descr')}</th>
                                    <th className="p-2 text-right">{t('pages.prev_col_qta')}</th>
                                    <th className="p-2 text-right">{t('pages.prev_col_prezzo')}</th>
                                    <th className="p-2 text-right">{t('pages.prev_col_totale')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(c.voci || []).map((v, i) => (
                                    <tr key={i} className="border-t border-[hsl(var(--border))]">
                                        <td className="p-2"><Badge variant="outline" className="text-xs">{v.tipo}</Badge></td>
                                        <td className="p-2">{v.descrizione}</td>
                                        <td className="p-2 text-right tabular-nums">{v.quantita} {v.unita}</td>
                                        <td className="p-2 text-right tabular-nums">€{v.prezzo_unitario}</td>
                                        <td className="p-2 text-right font-semibold tabular-nums">€{v.totale}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="md:hidden space-y-3">
                        {(c.voci || []).map((v, i) => (
                            <div key={i} className="p-3 rounded-lg border border-[hsl(var(--border))]">
                                <div className="flex items-center justify-between mb-1">
                                    <Badge variant="outline" className="text-xs">{v.tipo}</Badge>
                                    <span className="font-semibold tabular-nums">€{v.totale}</span>
                                </div>
                                <div className="text-sm mb-1">{v.descrizione}</div>
                                <div className="text-xs text-[hsl(var(--muted-foreground))] tabular-nums">{v.quantita} {v.unita} × €{v.prezzo_unitario}</div>
                            </div>
                        ))}
                    </div>

                    <div className="flex justify-end mt-6">
                        <div className="w-full sm:w-72 space-y-1 text-sm">
                            <div className="flex justify-between"><span>{t('pages.prev_detail_subtotale')}</span><span className="tabular-nums">€{c.subtotale}</span></div>
                            <div className="flex justify-between"><span>{t('pages.prev_form_iva')} {c.iva_percentuale}%</span><span className="tabular-nums">€{c.iva_importo}</span></div>
                            <div className="flex justify-between font-display text-2xl border-t border-[hsl(var(--border))] pt-2"><span>{t('pages.prev_detail_total')}</span><span className="tabular-nums" data-testid="public-quote-total">€{c.totale_finale}</span></div>
                        </div>
                    </div>

                    {c.tempi_stimati_giorni && (
                        <div className="mt-4 text-sm text-[hsl(var(--muted-foreground))]" dangerouslySetInnerHTML={sanitizeStrict(t('pages.prev_detail_tempi', { days: c.tempi_stimati_giorni }))} />
                    )}
                    {c.note && <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">{c.note}</p>}
                </Card>

                <div className="mt-6 text-center text-xs text-[hsl(var(--muted-foreground))] no-print">
                    {t('pages.prev_public_footer') || 'Preventivo generato con CantiereAI'}
                </div>
            </div>
        </div>
    );
}
